// Developed by Softvative.com
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import * as FaIcons from 'react-icons/fa';
import Input from '../ui/Input';
import Button from '../ui/Button';
import AuthLayout from './AuthLayout';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';
import config from '../../config/api';
import SafeIcon from '../../common/SafeIcon';

const { FiMail, FiLock, FiAlertCircle, FiShield, FiUsers, FiUser, FiCopy, FiCheck } = FiIcons;
const { FaGoogle, FaMicrosoft } = FaIcons;

const Login = () => {
    const navigate = useNavigate();
    const { signIn } = useAuth();
    const [formData, setFormData] = useState({
        email: '',
        password: '',
        rememberMe: false
    });
    const [loading, setLoading] = useState(false);
    const [copiedCredential, setCopiedCredential] = useState(null);

    const mockCredentials = [
        {
            role: 'Super Admin',
            email: 'superadmin@system.com',
            password: 'any',
            description: 'Full system access across all tenants',
            icon: FiShield,
            color: 'purple',
            features: ['Manage all tenants', 'View all users', 'System-wide dashboard', 'Bypass tenant restrictions']
        },
        {
            role: 'Tenant Owner',
            email: 'admin@acme.com',
            password: 'any',
            description: 'Admin access for Acme Corporation tenant',
            icon: FiUsers,
            color: 'blue',
            features: ['Manage tenant users', 'Create invites', 'Tenant dashboard', 'Configure settings']
        },
        {
            role: 'Tenant User',
            email: 'user@acme.com',
            password: 'any',
            description: 'Standard user access for Acme Corporation',
            icon: FiUser,
            color: 'green',
            features: ['View own profile', 'Work on incidents', 'Limited dashboard', 'Read-only tenant info']
        }
    ];

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const response = await signIn(formData.email, formData.password);
            if (!response.success) {
                toast.error(response.error || 'Login failed. Please try again.');
                navigate('/login');
            }
            navigate('/');
        } catch (error) {
            toast.error(error.message || 'Login failed. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleQuickLogin = async (email) => {
        setLoading(true);
        try {
            await signIn(email, 'any');
            toast.success('Login successful!');
            navigate('/');
        } catch (error) {
            toast.error(error.message || 'Login failed. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleCopyCredential = (email, credentialType) => {
        navigator.clipboard.writeText(email);
        setCopiedCredential(credentialType);
        toast.success(`${credentialType} credential copied!`);
        setTimeout(() => setCopiedCredential(null), 2000);
    };

    const getColorClasses = (color) => {
        const colors = {
            purple: {
                bg: 'bg-purple-50 dark:bg-purple-900/20',
                border: 'border-purple-200 dark:border-purple-800',
                text: 'text-purple-900 dark:text-purple-300',
                icon: 'text-purple-600 dark:text-purple-400',
                button: 'bg-purple-600 hover:bg-purple-700 text-white'
            },
            blue: {
                bg: 'bg-blue-50 dark:bg-blue-900/20',
                border: 'border-blue-200 dark:border-blue-800',
                text: 'text-blue-900 dark:text-blue-300',
                icon: 'text-blue-600 dark:text-blue-400',
                button: 'bg-blue-600 hover:bg-blue-700 text-white'
            },
            green: {
                bg: 'bg-green-50 dark:bg-green-900/20',
                border: 'border-green-200 dark:border-green-800',
                text: 'text-green-900 dark:text-green-300',
                icon: 'text-green-600 dark:text-green-400',
                button: 'bg-green-600 hover:bg-green-700 text-white'
            }
        };
        return colors[color];
    };

    return (
        <AuthLayout
            title="Welcome back"
            subtitle="Sign in to your account to continue"
        >
            {/* Development Mode Banner */}
            {config.isDevelopment && (
                <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-6"
                >
                    <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg mb-4">
                        <div className="flex items-start space-x-3">
                            <FiAlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
                            <div className="flex-1">
                                <h4 className="text-sm font-semibold text-amber-900 dark:text-amber-300 mb-1">
                                    🚀 Development Mode Active
                                </h4>
                                <p className="text-xs text-amber-700 dark:text-amber-400">
                                    Using mock data - No backend required. Choose a role below to login instantly!
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Mock Credentials Cards */}
                    <div className="space-y-3">
                        {mockCredentials.map((credential, index) => {
                            const colorClasses = getColorClasses(credential.color);
                            return (
                                <motion.div
                                    key={index}
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: index * 0.1 }}
                                    className={`p-4 ${colorClasses.bg} border ${colorClasses.border} rounded-lg`}
                                >
                                    <div className="flex items-start justify-between mb-3">
                                        <div className="flex items-center space-x-3">
                                            <div className={`p-2 bg-white dark:bg-gray-800 rounded-lg ${colorClasses.border} border`}>
                                                <SafeIcon icon={credential.icon} className={`w-5 h-5 ${colorClasses.icon}`} />
                                            </div>
                                            <div>
                                                <h5 className={`font-semibold ${colorClasses.text}`}>
                                                    {credential.role}
                                                </h5>
                                                <p className="text-xs text-gray-600 dark:text-gray-400">
                                                    {credential.description}
                                                </p>
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => handleQuickLogin(credential.email)}
                                            disabled={loading}
                                            className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${colorClasses.button} disabled:opacity-50`}
                                        >
                                            Quick Login
                                        </button>
                                    </div>

                                    <div className="grid grid-cols-2 gap-2 mb-3">
                                        <div className="flex items-center space-x-2 bg-white dark:bg-gray-800 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700">
                                            <FiMail className="w-3.5 h-3.5 text-gray-500" />
                                            <span className="text-xs font-mono text-gray-700 dark:text-gray-300 truncate">
                                                {credential.email}
                                            </span>
                                            <button
                                                onClick={() => handleCopyCredential(credential.email, credential.role)}
                                                className="ml-auto p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                                            >
                                                {copiedCredential === credential.role ? (
                                                    <FiCheck className="w-3.5 h-3.5 text-green-600" />
                                                ) : (
                                                    <FiCopy className="w-3.5 h-3.5 text-gray-400" />
                                                )}
                                            </button>
                                        </div>
                                        <div className="flex items-center space-x-2 bg-white dark:bg-gray-800 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700">
                                            <FiLock className="w-3.5 h-3.5 text-gray-500" />
                                            <span className="text-xs font-mono text-gray-700 dark:text-gray-300">
                                                {credential.password}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="flex flex-wrap gap-1.5">
                                        {credential.features.map((feature, idx) => (
                                            <span
                                                key={idx}
                                                className="px-2 py-1 text-xs bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-full text-gray-700 dark:text-gray-300"
                                            >
                                                {feature}
                                            </span>
                                        ))}
                                    </div>
                                </motion.div>
                            );
                        })}
                    </div>

                    <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
                        <p className="text-xs text-center text-gray-600 dark:text-gray-400">
                            💡 <strong>Tip:</strong> Or enter any credentials manually below - all passwords work in dev mode!
                        </p>
                    </div>
                </motion.div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
                <Input
                    label="Email address"
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="you@example.com"
                    icon={FiMail}
                    required
                />

                <Input
                    label="Password"
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="Enter your password"
                    icon={FiLock}
                    required
                />

                <div className="flex items-center justify-between">
                    <label className="flex items-center">
                        <input
                            type="checkbox"
                            name="rememberMe"
                            checked={formData.rememberMe}
                            onChange={handleChange}
                            className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                        />
                        <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">
                            Remember me
                        </span>
                    </label>

                    <Link
                        to="/forgot-password"
                        className="text-sm font-medium text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 dark:hover:text-indigo-300"
                    >
                        Forgot password?
                    </Link>
                </div>

                <Button
                    type="submit"
                    variant="primary"
                    fullWidth
                    loading={loading}
                >
                    Sign in
                </Button>

                <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-gray-300 dark:border-gray-600"></div>
                    </div>
                    <div className="relative flex justify-center text-sm">
                        <span className="px-2 bg-white dark:bg-gray-900 text-gray-500 dark:text-gray-400">
                            Or continue with
                        </span>
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                    <button
                        type="button"
                        className="flex items-center justify-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                        <FaGoogle className="w-5 h-5 mr-2" />
                        Google
                    </button>
                    <button
                        type="button"
                        className="flex items-center justify-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                        <FaMicrosoft className="w-5 h-5 mr-2" />
                        Microsoft
                    </button>
                </div>

                <p className="text-center text-sm text-gray-600 dark:text-gray-400">
                    Don't have an account?{' '}
                    <Link
                        to="/signup"
                        className="font-medium text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 dark:hover:text-indigo-300"
                    >
                        Sign up
                    </Link>
                </p>
            </form>
        </AuthLayout>
    );
};

export default Login;